// Phase 0b PR 11: OpsAuditTimeline.
//
// Read-only "what happened here" timeline over operational_audit_event rows.
// Drives the History tab on /delivery/programs/:id (the route path stays
// ops-audit). One table row per event - when / who / action / what changed -
// expanding to the untruncated before/after payload. The thread button opens
// the full set of events sharing one correlation_id so multi-step actions
// (CommitImport fans out into change_set.commit + N field_lineage writes,
// etc.) read as a thread. Paging is server-side: the RPC takes offset/limit
// and answers with the unpaged total.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronDown, ChevronRight, Link as LinkIcon } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useSearchTenantMembers } from '../../../Admin/Access/hooks';
import { useOpsAuditByCorrelation, useOpsAuditEvents } from '../hooks/lineageAuditHooks';
import { humanizeDiffs } from './opsAuditFormat';
const PAGE_SIZE = 25;
// Diffs rendered inline before the summary cell collapses to a count. Enough
// to read the gist of a multi-field write without the row growing into a wall
// of text; the rest is one click away in the detail area.
const SUMMARY_DIFF_LIMIT = 3;
// Value length shown in the summary cell. The detail area carries the
// untruncated payload, so eliding here costs nothing.
const SUMMARY_VALUE_LIMIT = 48;
function formatTimestamp(seconds) {
    if (!seconds) return '-';
    return new Date(Number(seconds) * 1000).toLocaleString();
}
// Policy decision -> semantic tone. allow is a pass (success); deny is a hard
// block (danger); conditional is a caveated allow (warning).
const POLICY_TONE = {
    allow: 'success',
    deny: 'danger',
    conditional: 'warning'
};
function truncateValue(value) {
    const trimmed = value.trim();
    if (!trimmed) return '(empty)';
    return trimmed.length > SUMMARY_VALUE_LIMIT ? `${trimmed.slice(0, SUMMARY_VALUE_LIMIT)}...` : trimmed;
}
// The "name shown, id in the hover tooltip, unresolved 32-hex ids shortened"
// rule, mirroring Admin's <ResolvedId>. Held locally rather than imported:
// that module's other export, useAdminNames, pulls in admin-only RPCs that
// 403 for exactly the restricted personas this timeline serves, and the
// names here are already resolved server-side.
function ResolvedActor({ id, name }) {
    // No actor id means the platform acted, not a person.
    if (!id) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "system"
    });
    // A name equal to the id is not a real name (the resolver echoed the id),
    // and a whitespace-only name would render a blank label - treat both as
    // unresolved so we fall back to the id rather than a self-referential
    // tooltip.
    if (name?.trim() && name !== id) {
        return /*#__PURE__*/ _jsx("span", {
            title: id,
            "data-resolved-id": id,
            children: name
        });
    }
    // Unresolved machine ids (32-hex) render shortened - a wall of full hashes
    // is unreadable; the full id stays one hover away.
    const shortened = /^[0-9a-f]{32}$/.test(id);
    return /*#__PURE__*/ _jsx("code", {
        className: "text-xs",
        title: shortened ? id : undefined,
        "data-resolved-id": id,
        children: shortened ? `${id.slice(0, 8)}...` : id
    });
}
function ActorCell({ event }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5",
        children: [
            /*#__PURE__*/ _jsx(ResolvedActor, {
                id: event.actorUserId,
                name: event.actorDisplayName
            }),
            event.actingOnBehalfOfUserId && /*#__PURE__*/ _jsxs("div", {
                className: "text-warning",
                title: "Delegated action - the actor held a designee grant; the action is ledgered against both identities",
                children: [
                    "on behalf of",
                    ' ',
                    /*#__PURE__*/ _jsx(ResolvedActor, {
                        id: event.actingOnBehalfOfUserId,
                        name: event.actingOnBehalfOfDisplayName
                    })
                ]
            })
        ]
    });
}
// truncate=false for the row-expand's "Changed fields" list: the summary cell
// elides long values because the row-expand exists to show the untruncated
// version one click away, so eliding there too would leave nowhere the full
// value is readable.
function DiffRow({ diff, truncate = true }) {
    const render = (v)=>truncate ? truncateValue(v) : v || '(empty)';
    return /*#__PURE__*/ _jsxs("div", {
        className: truncate ? 'truncate' : 'break-all',
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "font-medium",
                children: diff.label
            }),
            ":",
            ' ',
            diff.collapsed ? render(diff.after) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    render(diff.before),
                    " ",
                    '->',
                    " ",
                    render(diff.after)
                ]
            })
        ]
    });
}
// An empty diff list has two opposite readings - "this action changed
// nothing" and "this action changed something your view of the program
// withholds" - so the server tells us which via payloadsWithheld rather than
// leaving the reader to guess. Reporting a withheld diff as "no changes"
// would have the audit surface understating the record.
function DiffSummary({ diffs, withheld, referenceDisplayNames }) {
    // One row open at a time is the wrong model here - this is one cell of one
    // row, not a list of rows - so "expanded" is just "show everything" for
    // this cell.
    const [expanded, setExpanded] = useState(false);
    // Humanize BEFORE the empty check (SAD-125): the raw diff carries
    // snake_case keys and bookkeeping/false noise a viewer can't read, so the
    // summary renders friendly labels/values with the noise dropped, and any
    // identity/reference value the server resolved renders as a name. The full
    // raw payload stays in the row expand. A masked viewer's diffs (and its
    // referenceDisplayNames, always empty for a masked view) already arrived
    // blanked from the server, so this only ever sees entitled values.
    const humanized = humanizeDiffs(diffs, referenceDisplayNames);
    if (humanized.length === 0) {
        return withheld ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground italic",
            title: "This action recorded field changes, but your view of this program does not include their values.",
            children: "Changes withheld from your view"
        }) : /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "No field changes recorded"
        });
    }
    const shown = expanded ? humanized : humanized.slice(0, SUMMARY_DIFF_LIMIT);
    const hiddenCount = humanized.length - shown.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5",
        children: [
            shown.map((diff, i)=>/*#__PURE__*/ _jsx(DiffRow, {
                    diff: diff
                }, `${diff.label}-${i}`)),
            hiddenCount > 0 && /*#__PURE__*/ _jsxs("button", {
                type: "button",
                "data-testid": Testids.opsAudit.diffShowMore,
                className: "text-muted-foreground hover:text-foreground hover:underline",
                onClick: (e)=>{
                    // This cell sits inside a row whose onClick opens the row-expand
                    // detail area - a different affordance entirely. Without this the
                    // click would toggle both.
                    e.stopPropagation();
                    setExpanded(true);
                },
                children: [
                    "+",
                    hiddenCount,
                    " more"
                ]
            })
        ]
    });
}
function DetailField({ label, value, title }) {
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: "text-muted-foreground",
                children: [
                    label,
                    ": "
                ]
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "font-mono break-all",
                title: title,
                children: value || 'none'
            })
        ]
    });
}
function EventDetail({ event }) {
    const hasPayload = !!event.previousStateJson || !!event.newStateJson;
    const rawObject = `${event.objectType}/${event.objectId}`;
    // Object/Correlation id/Source/Event id stay raw - matt's own carve-out,
    // they help backend tracing - but the object itself resolves to a name the
    // same way actor_display_name does, so a viewer isn't stuck reading
    // "delivery.programs/<uuid>"; the raw path stays one hover away.
    const objectDisplay = event.objectDisplayName || rawObject;
    const humanizedFieldDiffs = humanizeDiffs(event.fieldDiffs, event.referenceDisplayNames);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.opsAudit.detail,
        "data-event-id": event.id,
        className: "space-y-3 bg-muted/30 p-3 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(DetailField, {
                        label: "Object",
                        value: objectDisplay,
                        title: event.objectDisplayName ? rawObject : undefined
                    }),
                    /*#__PURE__*/ _jsx(DetailField, {
                        label: "Source",
                        value: event.source
                    }),
                    /*#__PURE__*/ _jsx(DetailField, {
                        label: "Correlation id",
                        value: event.correlationId
                    }),
                    /*#__PURE__*/ _jsx(DetailField, {
                        label: "Event id",
                        value: event.id
                    })
                ]
            }),
            humanizedFieldDiffs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-0.5",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "font-medium",
                        children: "Changed fields"
                    }),
                    humanizedFieldDiffs.map((diff, i)=>/*#__PURE__*/ _jsx(DiffRow, {
                            diff: diff,
                            truncate: false
                        }, `${diff.label}-${i}`))
                ]
            }),
            hasPayload ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    event.previousStateJson && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium",
                                children: "Before"
                            }),
                            /*#__PURE__*/ _jsx("pre", {
                                className: "font-mono break-all whitespace-pre-wrap text-muted-foreground",
                                children: event.previousStateJson
                            })
                        ]
                    }),
                    event.newStateJson && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium",
                                children: "After"
                            }),
                            /*#__PURE__*/ _jsx("pre", {
                                className: "font-mono break-all whitespace-pre-wrap",
                                children: event.newStateJson
                            })
                        ]
                    })
                ]
            }) : event.payloadsWithheld ? /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "This action recorded before/after state, but your view of this program withholds the values. The action, the actor and the time are shown above."
            }) : /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "No before/after state was recorded for this event."
            })
        ]
    });
}
// One shared row shape for the timeline and for a correlation thread. `nested`
// drops the thread column - you are already inside one thread, and offering to
// open another from there just loops.
function EventTable({ events, programId, nested = false }) {
    // One row open at a time, keyed by event id (not correlation id - sibling
    // rows of the same operation share one).
    const [expandedId, setExpandedId] = useState(null);
    const [threadForId, setThreadForId] = useState(null);
    const columnCount = nested ? 4 : 5;
    return /*#__PURE__*/ _jsxs(Table, {
        children: [
            /*#__PURE__*/ _jsx(TableHeader, {
                children: /*#__PURE__*/ _jsxs(TableRow, {
                    children: [
                        /*#__PURE__*/ _jsx(TableHead, {
                            className: "w-48",
                            children: "When"
                        }),
                        /*#__PURE__*/ _jsx(TableHead, {
                            className: "w-48",
                            children: "Who"
                        }),
                        /*#__PURE__*/ _jsx(TableHead, {
                            className: "w-64",
                            children: "Action"
                        }),
                        /*#__PURE__*/ _jsx(TableHead, {
                            children: "Summary"
                        }),
                        !nested && /*#__PURE__*/ _jsx(TableHead, {
                            className: "w-32"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(TableBody, {
                children: events.map((event)=>{
                    const isExpanded = expandedId === event.id;
                    const isThreadOpen = threadForId === event.id;
                    const toggleExpand = ()=>setExpandedId(isExpanded ? null : event.id);
                    return /*#__PURE__*/ _jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(TableRow, {
                                "data-testid": Testids.opsAudit.row,
                                "data-event-id": event.id,
                                "data-action-key": event.actionKey,
                                className: "cursor-pointer align-top",
                                onClick: toggleExpand,
                                children: [
                                    /*#__PURE__*/ _jsxs(TableCell, {
                                        className: "text-xs whitespace-nowrap",
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                "aria-expanded": isExpanded,
                                                "aria-label": isExpanded ? 'Hide event detail' : 'Show event detail',
                                                "data-testid": Testids.opsAudit.expandToggle,
                                                className: "mr-1 align-middle text-muted-foreground",
                                                onClick: (e)=>{
                                                    // The row already toggles; without this the button's
                                                    // click bubbles up and toggles a second time.
                                                    e.stopPropagation();
                                                    toggleExpand();
                                                },
                                                children: isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                                    className: "h-3.5 w-3.5"
                                                }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                                    className: "h-3.5 w-3.5"
                                                })
                                            }),
                                            formatTimestamp(event.timestamp?.seconds)
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TableCell, {
                                        "data-testid": Testids.opsAudit.actorCell,
                                        className: "text-xs",
                                        children: /*#__PURE__*/ _jsx(ActorCell, {
                                            event: event
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TableCell, {
                                        className: "text-xs",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-wrap items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium",
                                                    children: event.actionKey
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: POLICY_TONE[event.policyDecision] ?? 'neutral',
                                                    children: event.policyDecision || 'unknown'
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TableCell, {
                                        "data-testid": Testids.opsAudit.diffCell,
                                        className: "text-xs",
                                        children: /*#__PURE__*/ _jsx(DiffSummary, {
                                            diffs: event.fieldDiffs,
                                            withheld: event.payloadsWithheld,
                                            referenceDisplayNames: event.referenceDisplayNames
                                        })
                                    }),
                                    !nested && /*#__PURE__*/ _jsx(TableCell, {
                                        className: "text-right",
                                        onClick: (e)=>e.stopPropagation(),
                                        children: event.correlationId && /*#__PURE__*/ _jsxs(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            onClick: ()=>setThreadForId(isThreadOpen ? null : event.id),
                                            "data-testid": Testids.opsAudit.threadButton,
                                            children: [
                                                /*#__PURE__*/ _jsx(LinkIcon, {
                                                    className: "mr-1 h-3.5 w-3.5"
                                                }),
                                                isThreadOpen ? 'Hide thread' : 'Show thread'
                                            ]
                                        })
                                    })
                                ]
                            }),
                            isExpanded && /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: columnCount,
                                    className: "p-0",
                                    children: /*#__PURE__*/ _jsx(EventDetail, {
                                        event: event
                                    })
                                })
                            }),
                            isThreadOpen && /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: columnCount,
                                    className: "p-0",
                                    children: /*#__PURE__*/ _jsx(CorrelationThread, {
                                        correlationId: event.correlationId,
                                        programId: programId
                                    })
                                })
                            })
                        ]
                    }, event.id);
                })
            })
        ]
    });
}
function CorrelationThread({ correlationId, programId }) {
    const { data: thread, isLoading } = useOpsAuditByCorrelation(correlationId, programId);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-3 text-xs text-muted-foreground",
            children: "Loading thread..."
        });
    }
    const events = thread?.events ?? [];
    if (events.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-3 text-xs text-muted-foreground",
            children: "No correlated events."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.opsAudit.threadPanel,
        className: "ml-4 border-l-2 border-muted pl-3 py-2",
        children: [
            thread?.truncated && /*#__PURE__*/ _jsxs("p", {
                className: "mb-2 text-xs text-warning",
                children: [
                    "Showing the ",
                    events.length,
                    " most recent of ",
                    Number(thread.total),
                    " events in this operation - its earliest steps are not shown."
                ]
            }),
            /*#__PURE__*/ _jsx(EventTable, {
                events: events,
                nested: true
            })
        ]
    });
}
// The Action filter's categories (SAD-777). The server hides only the
// resolver's access.* churn, so every recorded write reaches History; these
// group the rest by what a reader is looking for. Each category sends its
// action_key prefixes and the server matches any of them. "All actions" sends
// none. A new emitter shows under "All actions" until it is filed here.
const ACTION_CATEGORIES = [
    {
        key: '',
        label: 'All actions',
        prefixes: []
    },
    {
        key: 'contract',
        label: 'Contract & periods',
        prefixes: [
            'contract.',
            'contract_project.',
            // The contract_model keys that are not execution writes stay here; the
            // execution ones are filed under Deliverables & teams (SAD-788).
            'contract_model.contract_event',
            'contract_model.exercise_line',
            'contract_model.deliverable_settings',
            'program.',
            'period.',
            'optional_task',
            'place_of_performance.',
            'duty.',
            'glossary_'
        ]
    },
    {
        key: 'lines',
        label: 'Lines',
        prefixes: [
            'clin.',
            'contract_clin.'
        ]
    },
    {
        key: 'deliverables',
        label: 'Deliverables & teams',
        prefixes: [
            'deliverable',
            'task.',
            'attachment.',
            'capability.',
            'contact.',
            'company_assignment.',
            'staffing_line.',
            'program_role.',
            'role_assignment.',
            // SAD-788: execution-model writes belong with deliverables & teams, not
            // the contract/period shell. Kept as specific prefixes so other
            // contract_model.* keys are not pulled in by a bare "contract_model.".
            'contract_model.duty',
            'contract_model.schedule_rule',
            'contract_model.workstream',
            'contract_model.sow',
            'contract_model.occurrence',
            'contract_model.submission'
        ]
    },
    {
        key: 'review',
        label: 'Review & changes',
        prefixes: [
            'change_set.',
            'program_modification.',
            'discrepancy.',
            'review_item.',
            'deviation.',
            'risk.',
            'closeout'
        ]
    },
    {
        key: 'access',
        label: 'Access',
        prefixes: [
            'role.',
            'delegation.',
            'access_request.',
            'audit.'
        ]
    },
    {
        key: 'documents',
        label: 'Documents',
        prefixes: [
            'document.'
        ]
    }
];
// OpsAuditUserFilter replaces the raw "Actor user_id" box (matt: meaningless to
// a viewer) with a search-by-name people picker, resolving the id the query
// still filters on. Same shape as the delegate picker in
// DelegateAccessRequestDialog - a name search over SearchTenantMembers with a
// scrollable result list - so the two behave identically. The selected actor
// shows as a chip with a Clear; the id, never the name, is what leaves for the
// query.
function OpsAuditUserFilter({ selectedId, selectedName, onSelect, onClear }) {
    const [query, setQuery] = useState('');
    const canSearch = query.trim().length >= 2;
    const members = useSearchTenantMembers(query, {
        enabled: canSearch
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "audit-user",
                children: "User"
            }),
            selectedId ? /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        id: "audit-user",
                        "data-testid": Testids.opsAudit.filterUserSelected,
                        "data-user-id": selectedId,
                        children: selectedName || selectedId
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        className: "text-xs text-muted-foreground hover:text-foreground",
                        "data-testid": Testids.opsAudit.filterUserClear,
                        onClick: ()=>{
                            setQuery('');
                            onClear();
                        },
                        children: "Clear"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "audit-user",
                        value: query,
                        onChange: (e)=>setQuery(e.target.value),
                        placeholder: "Search people by name",
                        "data-testid": Testids.opsAudit.filterUserSearchInput
                    }),
                    canSearch && /*#__PURE__*/ _jsxs("div", {
                        className: "max-h-40 overflow-y-auto rounded-md border",
                        children: [
                            members.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "p-2 text-xs text-muted-foreground",
                                children: "Searching..."
                            }),
                            !members.isLoading && (members.data ?? []).length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "p-2 text-xs text-muted-foreground",
                                children: "No matching people."
                            }),
                            (members.data ?? []).map((m)=>/*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    "data-testid": Testids.opsAudit.filterUserOption,
                                    "data-user-id": m.id,
                                    className: "block w-full px-2 py-1.5 text-left text-xs hover:bg-accent",
                                    onClick: ()=>{
                                        setQuery('');
                                        onSelect(m.id, m.displayName);
                                    },
                                    children: m.displayName
                                }, m.id))
                        ]
                    })
                ]
            })
        ]
    });
}
export function OpsAuditTimeline({ defaultObjectType, defaultObjectId }) {
    const [filters, setFilters] = useState({
        objectType: defaultObjectType,
        objectId: defaultObjectId
    });
    const [pendingFilters, setPendingFilters] = useState(filters);
    const [page, setPage] = useState(0);
    // The actor filter now travels as an id, but the picker shows a name - held
    // here so the selected chip survives re-renders without a name lookup.
    const [selectedUser, setSelectedUser] = useState(null);
    // isPlaceholderData means the hook is handing back the PREVIOUS query's rows
    // while the new offset/filter set is still in flight (keepPreviousData keeps
    // the table from unmounting into a spinner on every page step). Those rows
    // answer a question nobody is asking any more, so pairing them with a window
    // computed from the new offset would put "Showing 26-50 of 83" over page
    // one's rows and leave the pager live on a position that does not exist yet.
    // Treat it as the transition it is.
    const { data, isLoading, isPlaceholderData, error } = useOpsAuditEvents({
        ...filters,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE
    });
    // Any change to what is being asked for invalidates the position in the
    // old result set - page 3 of a different query is meaningless.
    const applyFilters = ()=>{
        setFilters(pendingFilters);
        setPage(0);
    };
    const clearFilters = ()=>{
        const reset = {
            objectType: defaultObjectType,
            objectId: defaultObjectId
        };
        setPendingFilters(reset);
        setFilters(reset);
        setSelectedUser(null);
        setPage(0);
    };
    // The program-scoped correlation lane only means anything when this
    // timeline IS a program's, so the id only travels under that object type.
    const programId = defaultObjectType === 'delivery.programs' ? defaultObjectId : undefined;
    // total is the unpaged count, so the window bounds come from it and the
    // page offset rather than from the row count on screen.
    const shown = data?.events.length ?? 0;
    const total = Number(data?.total ?? 0);
    const firstRow = page * PAGE_SIZE + 1;
    const lastRow = page * PAGE_SIZE + shown;
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.opsAudit.panel,
        "aria-busy": isPlaceholderData,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex-col items-start gap-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "History"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "What happened on this program - every state change with who made it, what it changed, and the policy decision behind it. Read-only."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsx(OpsAuditUserFilter, {
                                selectedId: selectedUser?.id,
                                selectedName: selectedUser?.name,
                                onSelect: (id, name)=>{
                                    setSelectedUser({
                                        id,
                                        name
                                    });
                                    setPendingFilters((prev)=>({
                                            ...prev,
                                            actorUserId: id
                                        }));
                                },
                                onClear: ()=>{
                                    setSelectedUser(null);
                                    setPendingFilters((prev)=>({
                                            ...prev,
                                            actorUserId: ''
                                        }));
                                }
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "audit-action",
                                        children: "Action"
                                    }),
                                    /*#__PURE__*/ _jsx("select", {
                                        id: "audit-action",
                                        className: "h-9 w-full rounded-md border border-input bg-background px-3 text-sm",
                                        value: pendingFilters.actionCategory ?? '',
                                        onChange: (e)=>{
                                            const key = e.target.value;
                                            const category = ACTION_CATEGORIES.find((c)=>c.key === key);
                                            setPendingFilters((prev)=>({
                                                    ...prev,
                                                    actionCategory: key,
                                                    actionKeyPrefixes: category ? [
                                                        ...category.prefixes
                                                    ] : []
                                                }));
                                        },
                                        "data-testid": Testids.opsAudit.filterActionSelect,
                                        children: ACTION_CATEGORIES.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                value: c.key,
                                                children: c.label
                                            }, c.key || 'all'))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: applyFilters,
                                "data-testid": Testids.opsAudit.applyFiltersButton,
                                children: "Apply Filters"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: clearFilters,
                                "data-testid": Testids.opsAudit.clearFiltersButton,
                                children: "Clear"
                            })
                        ]
                    }),
                    isLoading ? /*#__PURE__*/ _jsx("div", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading history..."
                    }) : error ? /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-destructive",
                        children: [
                            "Failed to load history: ",
                            error instanceof Error ? error.message : 'Unknown error'
                        ]
                    }) : shown === 0 ? /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.opsAudit.emptyState,
                        className: "rounded-lg border border-dashed px-6 py-10 text-center",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-medium text-foreground",
                                children: "No history to show"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mx-auto mt-1 max-w-md text-sm text-muted-foreground",
                                children: "This log fills in automatically as actions are taken on the program - imports, state changes, and access decisions. Nothing matches the current filters yet; widen or clear them to see more."
                            })
                        ]
                    }) : // Dimmed while retained: the rows are last query's answer, and the
                    // reader should be able to see that at a glance rather than infer it.
                    /*#__PURE__*/ _jsx("div", {
                        className: isPlaceholderData ? 'opacity-50' : undefined,
                        children: /*#__PURE__*/ _jsx(EventTable, {
                            events: data?.events ?? [],
                            programId: programId
                        })
                    }),
                    !isLoading && !error && (shown > 0 || page > 0) && /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.opsAudit.pageStatus,
                                className: "text-xs text-muted-foreground",
                                children: isPlaceholderData ? 'Updating...' : shown === 0 ? `Showing 0 of ${total}` : `Showing ${firstRow}-${lastRow} of ${total}`
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        disabled: page === 0 || isPlaceholderData,
                                        onClick: ()=>setPage((prev)=>Math.max(0, prev - 1)),
                                        "data-testid": Testids.opsAudit.pagerPrev,
                                        children: "Previous"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        disabled: lastRow >= total || isPlaceholderData,
                                        onClick: ()=>setPage((prev)=>prev + 1),
                                        "data-testid": Testids.opsAudit.pagerNext,
                                        children: "Next"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
